import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  useToast,
  Box,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import SavingsPlanStoreApi from 'api/savingplanstore';
import SavingPlanVariantApi from 'api/savingplanvariant';
import StoresApi from 'api/stores';
import InputField from 'components/formComponents/inputField';
import SelectField from 'components/formComponents/selectField';
import MultiLineSelectField from 'components/formComponents/multiLineSelectField';

export default function PopModal(props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialRef = React.useRef(null);
  const finalRef = React.useRef(null);
  const toast = useToast();
  const [storeList, setStoreList] = useState([]);
  const [variantList, setVariantList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [listTotal, setListTotal] = useState(2);
  const initialValues = {
    companyId: '',
    planId: '',
    variantId: '',
    storeId: '',
    displayOrder: 1,
  };

  const validationSchema = Yup.object().shape({
    companyId: Yup.string().required('Company is required'),
    planId: Yup.string().required('Saving Plan is required'),
    variantId: Yup.string().required('Saving Plan Variant is required'),
    storeId: Yup.string().required('Branch is required'),
    displayOrder: Yup.number()
      .required('Display order is required')
      .positive('Display order must be positive')
      .integer('Display order must be an integer'),
  });

  const add = async (values, actions) => {
    const { companyId, planId, variantId, storeId, displayOrder } = values;

    try {
      const api = await SavingsPlanStoreApi.Create({
        store_id: storeId,
        saving_plan_variant_id: variantId,
        is_available: true,
        is_active: true,
        display_order: Number(displayOrder),
      });

      const response = api.data;
      if (response.status_code === 200) {
        toast({
          title: response?.message || 'Successfully Added',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        onClose();
        props.func();
      } else {
        toast({
          title: response?.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const FetchStoreList = async (companyId, page = 1) => {
    try {
      let response = await StoresApi.List({
        companyId,
        sort: 'name:asc',
        page,
        itemsPerPage: 100,
        saving_plan_enable: true,
      });
      response = response.data;
      if (response.status_code === 200 && response?.data?.list?.length > 0) {
        setStoreList(response?.data?.list);
        setListTotal(response?.data?.total);
      } else {
        setStoreList([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchVariantList = async (planId, page = 1) => {
    try {
      let response = await SavingPlanVariantApi.List({
        planId,
        sort: 'name:asc',
        page,
      });
      response = response.data;
      if (response.status_code === 200 && response?.data?.list?.length > 0) {
        setVariantList(response?.data?.list);
        setTotalPages(response?.data?.total_pages);
      } else {
        setVariantList([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (initialValues.companyId) {
      FetchStoreList(initialValues.companyId, currentPage);
    }
    if (initialValues.planId) {
      fetchVariantList(initialValues.planId, currentPage);
    }
  }, [initialValues.companyId, initialValues.planId, currentPage]);

  return (
    <>
      <Button
        variant='lightBrand'
        fontSize='sm'
        fontWeight='500'
        borderRadius='10px'
        boxShadow='lg'
        px='15px'
        py='5px'
        disabled={props.disabled}
        onClick={onOpen}
      >
        {props.Icon}
      </Button>
      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{props.head}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={add}
            >
              {({ isSubmitting, setFieldValue }) => (
                <Form>
                  <Flex direction='column'>
                    <SelectField
                      name='companyId'
                      label='Company'
                      options={props.companiesList.map((company) => ({
                        label: `${company.name} (${company.company_code})`,
                        value: company.id,
                      }))}
                      placeholder='Select Parent Company of Branch.'
                      required
                      onChange={(e) => {
                        setFieldValue('companyId', e.target.value);
                        FetchStoreList(e.target.value);
                      }}
                    />
                    <SelectField
                      name='planId'
                      label='Saving Plan'
                      options={props.planList.map((plan) => ({
                        label: plan.name,
                        value: plan.category,
                      }))}
                      placeholder='Select Saving Plan'
                      required
                      onChange={(e) => {
                        setFieldValue('planId', e.target.value);
                        fetchVariantList(e.target.value);
                      }}
                    />
                    <SelectField
                      name='variantId'
                      label='Saving Plan Variant'
                      options={variantList.map((variant) => ({
                        label: `${variant.period} Months`,
                        value: variant.id,
                      }))}
                      placeholder='Select Saving Plan Variant'
                      required
                    />
                    <MultiLineSelectField
                      name='storeId'
                      label='Branch Name'
                      options={storeList.map((store) => ({
                        titleLabel: store.name,
                        subLabel: `( ${store.store_code} )`,
                        bodyLabel: `${store.city} ${store.state} ${store.pincode}`,
                        value: store.id,
                      }))}
                      placeholder='Select Branch.'
                      required
                    />
                    <InputField
                      name='displayOrder'
                      label='Display Order'
                      type='number'
                      placeholder='Enter Display Order'
                      required
                    />
                  </Flex>
                  <ModalFooter>
                    <Button
                      colorScheme='blue'
                      mr={3}
                      type='submit'
                      isLoading={isSubmitting}
                    >
                      Save
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
